import { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';

// Hosts that, in our experience, serve LMS pages or VTT captions for the LMSes
// we support. The Videos route surfaces a permission row per host so the user
// can grant access on demand. We default to "missing" until proven otherwise.
const KNOWN_HOSTS: { label: string; pattern: string; why: string }[] = [
  {
    label: 'CeriFi LMS',
    pattern: 'https://*.cerifi.com/*',
    why: 'Page host — Vanguard prep lessons.',
  },
  {
    label: 'Widen CDN (captions)',
    pattern: 'https://*.widencdn.net/*',
    why: 'Where CeriFi serves video subtitle .vtt files.',
  },
  {
    label: 'Widen (videos)',
    pattern: 'https://*.widen.net/*',
    why: 'Video player iframe origin.',
  },
];

export function PermissionsPanel() {
  const [granted, setGranted] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Webapp context has no chrome.permissions API — host grants are an
  // extension-only concept. Render nothing rather than crash.
  const hasPermissionsApi =
    typeof chrome !== 'undefined' && typeof chrome.permissions !== 'undefined';

  async function refresh() {
    if (!hasPermissionsApi) return;
    const out: Record<string, boolean> = {};
    for (const h of KNOWN_HOSTS) {
      out[h.pattern] = await chrome.permissions.contains({ origins: [h.pattern] });
    }
    setGranted(out);
  }

  useEffect(() => {
    if (!hasPermissionsApi) return;
    void refresh();
    const onChange = () => void refresh();
    const added = chrome.permissions.onAdded as unknown as chrome.events.Event<() => void>;
    const removed = chrome.permissions.onRemoved as unknown as chrome.events.Event<() => void>;
    added.addListener(onChange);
    removed.addListener(onChange);
    return () => {
      added.removeListener(onChange);
      removed.removeListener(onChange);
    };
  }, [hasPermissionsApi]);

  if (!hasPermissionsApi) return null;

  // Grant must happen synchronously off the click — no awaits before
  // chrome.permissions.request, or Chrome rejects with "must be called during
  // a user gesture".
  function grant(pattern: string) {
    setError(null);
    chrome.permissions
      .request({ origins: [pattern] })
      .then((ok) => {
        if (!ok) setError(`Chrome denied access to ${pattern}.`);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
      });
  }

  const missing = KNOWN_HOSTS.filter((h) => granted[h.pattern] === false);
  const hasMissing = missing.length > 0;

  return (
    <div className="rounded border border-slate-200 bg-slate-50 text-xs">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="font-medium text-slate-700">
          Host access {hasMissing ? '· grant needed' : '· all set'}
        </span>
        <Badge tone={hasMissing ? 'warning' : 'success'}>
          {hasMissing ? `${missing.length} missing` : 'OK'}
        </Badge>
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-200 px-3 py-2">
          <p className="text-slate-600">
            VTT subtitles are only seen on hosts Chrome has granted us access to. CeriFi
            videos are served through Widen's CDN, so we need both granted.
          </p>
          {KNOWN_HOSTS.map((h) => (
            <div key={h.pattern} className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800">{h.label}</div>
                <div className="text-[10px] text-slate-500">{h.why}</div>
                <code className="block break-all text-[10px] text-slate-400">{h.pattern}</code>
              </div>
              {granted[h.pattern] ? (
                <Badge tone="success">granted</Badge>
              ) : (
                <Button variant="primary" size="xs" onClick={() => grant(h.pattern)}>
                  Grant
                </Button>
              )}
            </div>
          ))}
          {error && <Alert>{error}</Alert>}
          <p className="text-[10px] text-slate-500">
            After granting, replay the video — the listener only sees requests made
            <em> after </em>permission is granted.
          </p>
        </div>
      )}
    </div>
  );
}
